/**
 * GTM DataLayer Tracking
 *
 * Isolated Google Tag Manager event tracking for the estimator wizard.
 * All dataLayer pushes are centralized here for separation of concerns.
 *
 * Every push goes through `push()`, which merges the tracking CONTEXT into
 * the payload. The context is what makes one shared build serve dozens of
 * retailers without a line of retailer-specific code: partner slug/id come
 * from the retailer row that matched the hostname, the price book from
 * whatever loadProducts() hydrated, the currency from the retailer's policy,
 * the environment from the hostname pattern, and the locale from i18n. A new
 * retailer configured in admin is fully tracked with zero code changes.
 *
 * Nothing pushed here may be PII: no email, name, phone or street address.
 */

import { CalculationMode, WizardStep, EstimateOutput, Currency, TotalFormat } from '../types';

// Initialize dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}
window.dataLayer = window.dataLayer || [];

// ============================================================================
// TRACKING CONTEXT — merged into every event
// ============================================================================

export type TrackingEnvironment = 'production' | 'beta' | 'dev';
export type PriceBook = 'retailer' | 'msrp' | 'fallback';

export interface TrackingContext {
  /** Retailer subdomain from admin; 'trusscore' on the apex site. */
  partnerSlug: string;
  partnerId: number | null;
  /** Retailer whose price book is loaded (1 = Trusscore MSRP). */
  retailerId: number | null;
  priceBook: PriceBook;
  environment: TrackingEnvironment;
  /** Display currency chosen by the retailer's policy or the visitor. */
  currency: Currency;
  /** How the on-screen Estimated Total is rounded. */
  totalFormat: TotalFormat;
  locale: string;
}

const context: TrackingContext = {
  partnerSlug: 'trusscore',
  partnerId: null,
  retailerId: null,
  priceBook: 'fallback',
  environment: 'production',
  currency: 'USD',
  totalFormat: 'rounded',
  locale: 'en',
};

/** Keys every event carries. Snake_case to match the rest of the dataLayer. */
function baseContext(): Record<string, unknown> {
  return {
    partner_slug: context.partnerSlug,
    partner_id: context.partnerId,
    retailer_id: context.retailerId,
    price_book: context.priceBook,
    environment: context.environment,
    currency: context.currency,
    locale: context.locale,
  };
}

/**
 * Update the context. Partner and environment changes are also pushed as a
 * context-only record (no `event`) so GTM data-layer variables hold them
 * before the first event fires — Gerson's "partner context at boot".
 */
export function setTrackingContext(patch: Partial<TrackingContext>): void {
  const before = { ...context };
  Object.assign(context, patch);
  const identityChanged =
    before.partnerSlug !== context.partnerSlug
    || before.partnerId !== context.partnerId
    || before.environment !== context.environment
    || before.retailerId !== context.retailerId;
  if (identityChanged) pushTrackingContext();
}

/** Push the current context with no event, for GTM variables to pick up. */
export function pushTrackingContext(): void {
  window.dataLayer.push(baseContext());
}

/** Read-only snapshot, for callers that need to build a path or label. */
export function getTrackingContext(): Readonly<TrackingContext> {
  return context;
}

function push(payload: Record<string, unknown>): void {
  window.dataLayer.push({ ...baseContext(), ...payload });
}

// ============================================================================
// HELPERS
// ============================================================================

type ProjectType = 'full_room' | 'ceiling_only' | 'individual_walls';

const PROJECT_TYPE_MAP: Record<CalculationMode, ProjectType> = {
  FullRoom: 'full_room',
  CeilingOnly: 'ceiling_only',
  IndividualWalls: 'individual_walls',
};

/** Step name labels used in dataLayer events */
const STEP_NAME_MAP: Record<WizardStep, string> = {
  mode: 'Project Type',
  walls: 'Walls',
  ceiling: 'Ceiling',
  dimensions: 'Dimensions',
  application: 'Application',
  leadCapture: 'Lead Capture',
};

/** Step orders per calculation mode */
const STEP_ORDERS: Record<CalculationMode, WizardStep[]> = {
  FullRoom: ['mode', 'walls', 'ceiling', 'dimensions', 'application'],
  CeilingOnly: ['mode', 'ceiling'],
  IndividualWalls: ['mode', 'walls', 'ceiling', 'application'],
};

/** Virtual page path slug per step, keyed by mode then step */
const PAGE_PATH_MAP: Record<CalculationMode, Record<string, string>> = {
  FullRoom: {
    mode: '/estimator/full-room/step-1-project-type',
    walls: '/estimator/full-room/step-2-walls',
    ceiling: '/estimator/full-room/step-3-ceiling',
    dimensions: '/estimator/full-room/step-4-dimensions',
    application: '/estimator/full-room/step-5-application',
    results: '/estimator/full-room/results',
  },
  CeilingOnly: {
    mode: '/estimator/ceiling-only/step-1-project-type',
    ceiling: '/estimator/ceiling-only/step-2-ceiling',
    results: '/estimator/ceiling-only/results',
  },
  IndividualWalls: {
    mode: '/estimator/individual-walls/step-1-project-type',
    walls: '/estimator/individual-walls/step-2-walls',
    ceiling: '/estimator/individual-walls/step-3-ceiling',
    application: '/estimator/individual-walls/step-4-application',
    results: '/estimator/individual-walls/results',
  },
};

function getStepNumber(mode: CalculationMode, step: WizardStep): number {
  return STEP_ORDERS[mode].indexOf(step) + 1;
}

function getFormCompletionPercent(mode: CalculationMode, step: WizardStep): number {
  const order = STEP_ORDERS[mode];
  const idx = order.indexOf(step);
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / order.length) * 100);
}

/**
 * Virtual page paths are prefixed with the partner slug on partner sites so
 * GA4 page reports separate each retailer's estimator from the apex one. The
 * slug is the retailer's subdomain from admin — never a hardcoded label.
 */
function partnerPagePath(path: string): string {
  return context.partnerSlug === 'trusscore' ? path : `/${context.partnerSlug}${path}`;
}

/** Panel + sqft totals shared by several events. */
function bomTotals(estimateResult: EstimateOutput): { totalPanels: number; totalSqft: number } {
  const bom = estimateResult.billOfMaterials;
  const totalPanels =
    bom.wallAndCeilingBoard.reduce((s, i) => s + i.quantity, 0) +
    bom.slatwall.reduce((s, i) => s + i.quantity, 0);
  const totalSqft =
    bom.wallAndCeilingBoard.reduce((s, i) => s + i.sqft * i.quantity, 0) +
    bom.slatwall.reduce((s, i) => s + i.sqft * i.quantity, 0);
  return { totalPanels, totalSqft: Math.round(totalSqft) };
}

/** Round the way the Estimated Total card does, so `estimated_cost` is the figure on screen. */
function displayedAmount(amount: number): number {
  if (context.totalFormat === 'precise') return Math.round(amount * 100) / 100;
  return Math.ceil(amount / 100) * 100;
}

/**
 * Price keys pushed wherever a cost is reported.
 *
 *   estimated_cost_usd / estimated_cost_cad — both columns of the loaded price
 *     book, rounded to the dollar. Pushed on every event so BigQuery never
 *     sees a half-populated column; the retailer sells in USD, CAD or both.
 *   estimated_cost — the total the visitor actually saw (display currency,
 *     display rounding).
 *   currency / price_book — come from the base context.
 */
function priceKeys(estimateResult: EstimateOutput): Record<string, unknown> {
  const usd = estimateResult.totals.totalCostUSD;
  const cad = estimateResult.totals.totalCostCAD;
  return {
    estimated_cost_usd: Math.round(usd),
    estimated_cost_cad: Math.round(cad),
    estimated_cost: displayedAmount(context.currency === 'CAD' ? cad : usd),
  };
}

// ============================================================================
// EVENT 1 — Project type selected
// ============================================================================

export function trackProjectTypeSelected(mode: CalculationMode): void {
  push({
    event: 'estimator_project_type_selected',
    project_type: PROJECT_TYPE_MAP[mode],
    step_number: 1,
    step_name: 'Project Type',
  });
}

// ============================================================================
// EVENT 2 — Step navigation
// ============================================================================

export function trackStepNavigation(
  action: 'next' | 'previous',
  fromStep: WizardStep,
  toStep: WizardStep,
  mode: CalculationMode,
): void {
  push({
    event: 'estimator_step_navigation',
    navigation_action: action,
    from_step: getStepNumber(mode, fromStep),
    to_step: getStepNumber(mode, toStep),
    step_name_from: STEP_NAME_MAP[fromStep],
    step_name_to: STEP_NAME_MAP[toStep],
    project_type: PROJECT_TYPE_MAP[mode],
    form_completion_percent: getFormCompletionPercent(mode, toStep),
  });
}

// ============================================================================
// EVENT 3 — Virtual page view
// ============================================================================

export function trackVirtualPageView(
  step: WizardStep | 'results',
  mode: CalculationMode,
): void {
  const basePath = PAGE_PATH_MAP[mode]?.[step] || `/estimator/${PROJECT_TYPE_MAP[mode]}/${step}`;
  const stepNum = step === 'results' ? null : getStepNumber(mode, step as WizardStep);
  const pageTitle =
    step === 'results'
      ? 'Estimator - Results'
      : `Estimator - Step ${stepNum} - ${STEP_NAME_MAP[step as WizardStep]}`;

  push({
    event: 'virtual_page_view',
    page_path: partnerPagePath(basePath),
    page_title: pageTitle,
    project_type: PROJECT_TYPE_MAP[mode],
  });
}


// ============================================================================
// EVENT 4 — Calculate estimate clicked
// ============================================================================

export interface CalculateClickedData {
  mode: CalculationMode;
  wallColor: string;
  wallOrientation: string;
  slatwallEnabled: boolean;
  ceilingIncluded: boolean;
  ceilingColor: string;
  spaceType: string;
  garageDoorEnabled: boolean;
  garageDoorCount: number;
  garageDoorSize: string;
  estimateResult: EstimateOutput;
}

export function trackCalculateClicked(data: CalculateClickedData): void {
  const { estimateResult } = data;

  // Compute wall area from surfaces
  const totalWallAreaSqft = estimateResult.surfaces.walls.reduce((sum, wall) => {
    const panelSqft = wall.materials.panels.reduce(
      (s, p) => s + p.sqft * p.quantity,
      0,
    );
    return sum + panelSqft;
  }, 0);

  // Compute ceiling area from surfaces
  const ceilingAreaSqft = estimateResult.surfaces.ceiling
    ? estimateResult.surfaces.ceiling.materials.panels.reduce(
        (s, p) => s + p.sqft * p.quantity,
        0,
      )
    : 0;

  const { totalPanels, totalSqft } = bomTotals(estimateResult);

  // Raw room dimensions (Step 4) — resolved from preset card or custom inputs.
  // Pushed as raw numbers so GA4/BigQuery can average and bucket them; the
  // formatted "40' L × 20' W × 10' H" string can't be aggregated. Length is
  // measured across the joists per the on-screen note.
  const { length, width, height } = estimateResult.input.roomDimensions;

  push({
    event: 'estimator_calculate_clicked',
    project_type: PROJECT_TYPE_MAP[data.mode],
    wall_color: (data.wallColor ?? '').toLowerCase(),
    wall_orientation: (data.wallOrientation ?? '').toLowerCase(),
    slatwall_enabled: data.slatwallEnabled,
    ceiling_included: data.ceilingIncluded,
    ceiling_color: (data.ceilingColor ?? '').toLowerCase(),
    space_type: data.spaceType,
    garage_door_enabled: data.garageDoorEnabled,
    garage_door_count: data.garageDoorCount,
    garage_door_size: data.garageDoorSize,
    total_wall_area_sqft: Math.round(totalWallAreaSqft),
    ceiling_area_sqft: Math.round(ceilingAreaSqft),
    room_length_ft: length,
    room_width_ft: width,
    room_height_ft: height,
    ...priceKeys(estimateResult),
    total_panels: totalPanels,
    total_sqft: totalSqft,
    form_completion_percent: 100,
  });
}

// ============================================================================
// EVENT 5 — Results page viewed
// ============================================================================

export function trackResultsViewed(
  mode: CalculationMode,
  spaceType: string,
  estimateResult: EstimateOutput,
): void {
  const { totalPanels, totalSqft } = bomTotals(estimateResult);

  push({
    event: 'estimator_results_viewed',
    project_type: PROJECT_TYPE_MAP[mode],
    total_panels: totalPanels,
    total_sqft: totalSqft,
    ...priceKeys(estimateResult),
    space_type: spaceType,
  });
}

// ============================================================================
// EVENT 6 — Find a Retailer clicked
// ============================================================================
// Fires for the built-in "find-retailer" link AND for every map-type action
// button (a retailer's store locator configured in admin), so the event name
// Gerson's GTM tag already matches keeps working for any retailer.

export function trackFindRetailerClick(
  mode: CalculationMode,
  spaceType: string,
  estimateResult: EstimateOutput | null,
  extra: { buttonId: string; mapId?: number | string | null } = { buttonId: 'find-retailer' },
): void {
  push({
    event: 'estimator_find_retailer_click',
    project_type: PROJECT_TYPE_MAP[mode],
    ...(estimateResult ? priceKeys(estimateResult) : {}),
    space_type: spaceType,
    button_id: extra.buttonId,
    map_id: extra.mapId ?? null,
  });
}

// ============================================================================
// EVENT 6b — Any Next Steps action button clicked
// ============================================================================
// The Next Steps bar is admin-configured per retailer. One generic event with
// the button's own id/type/label covers every retailer's buttons, present and
// future, without a new event per button. `button_label` is admin text.

export function trackActionButtonClick(
  mode: CalculationMode,
  spaceType: string,
  estimateResult: EstimateOutput | null,
  button: { id: string; actionType: string; label: string; url?: string | null; mapId?: number | string | null },
): void {
  push({
    event: 'estimator_action_button_click',
    project_type: PROJECT_TYPE_MAP[mode],
    space_type: spaceType,
    ...(estimateResult ? priceKeys(estimateResult) : {}),
    button_id: button.id,
    action_type: button.actionType,
    button_label: button.label,
    link_url: button.url ?? null,
    map_id: button.mapId ?? null,
  });
}

// ============================================================================
// EVENT 7 — Wall color selected
// ============================================================================

export function trackWallColorSelected(
  mode: CalculationMode,
  wallColor: string,
  stepNumber: number,
): void {
  push({
    event: 'estimator_wall_color_selected',
    project_type: PROJECT_TYPE_MAP[mode],
    wall_color: (wallColor ?? '').toLowerCase(),
    step_number: stepNumber,
    step_name: 'Walls',
  });
}

// ============================================================================
// EVENT 8 — Wall orientation selected
// ============================================================================

export function trackWallOrientationSelected(
  mode: CalculationMode,
  wallOrientation: string,
  stepNumber: number,
): void {
  push({
    event: 'estimator_wall_orientation_selected',
    project_type: PROJECT_TYPE_MAP[mode],
    wall_orientation: (wallOrientation ?? '').toLowerCase(),
    step_number: stepNumber,
    step_name: 'Walls',
  });
}

// ============================================================================
// EVENT 9 — SlatWall toggled
// ============================================================================

export function trackSlatwallToggled(
  mode: CalculationMode,
  slatwallEnabled: boolean,
  stepNumber: number,
): void {
  push({
    event: 'estimator_slatwall_toggled',
    project_type: PROJECT_TYPE_MAP[mode],
    slatwall_enabled: slatwallEnabled,
    step_number: stepNumber,
    step_name: 'Walls',
  });
}

// ============================================================================
// EVENT 10 — Ceiling configured
// ============================================================================

export function trackCeilingConfigured(
  mode: CalculationMode,
  ceilingIncluded: boolean,
  ceilingColor: string | null,
  stepNumber: number,
): void {
  push({
    event: 'estimator_ceiling_configured',
    project_type: PROJECT_TYPE_MAP[mode],
    ceiling_included: ceilingIncluded,
    ceiling_color: ceilingIncluded && ceilingColor ? ceilingColor.toLowerCase() : null,
    step_number: stepNumber,
    step_name: 'Ceiling',
  });
}

// ============================================================================
// EVENT 11 — Space type selected
// ============================================================================

export function trackSpaceTypeSelected(
  mode: CalculationMode,
  spaceType: string,
  stepNumber: number,
): void {
  push({
    event: 'estimator_space_type_selected',
    project_type: PROJECT_TYPE_MAP[mode],
    space_type: spaceType,
    step_number: stepNumber,
    step_name: 'Application',
  });
}

// ============================================================================
// EVENT 12 — Garage door toggled
// ============================================================================

export function trackGarageDoorToggled(
  mode: CalculationMode,
  garageDoorEnabled: boolean,
  stepNumber: number,
): void {
  push({
    event: 'estimator_garage_door_toggled',
    project_type: PROJECT_TYPE_MAP[mode],
    garage_door_enabled: garageDoorEnabled,
    step_number: stepNumber,
    step_name: 'Application',
  });
}

// ============================================================================
// EVENT 13 — Preset size selected
// ============================================================================

export function trackPresetSelected(
  mode: CalculationMode,
  presetName: string,
  presetType: 'room_size' | 'garage_door_size',
  stepNumber: number,
): void {
  push({
    event: 'estimator_preset_selected',
    project_type: PROJECT_TYPE_MAP[mode],
    preset_name: presetName,
    preset_type: presetType,
    step_number: stepNumber,
    step_name: 'Dimensions',
  });
}

// ============================================================================
// EVENT 14 — Individual wall configured
// ============================================================================

export function trackWallConfigured(
  wallIndex: number,
  wallName: string,
  wallColor: string,
  wallOrientation: string,
  wallLength: number,
  wallHeight: number,
  slatwallEnabled: boolean,
  garageDoorIncluded: boolean,
  totalWalls: number,
): void {
  push({
    event: 'estimator_wall_configured',
    project_type: 'individual_walls',
    wall_index: wallIndex,
    wall_name: wallName,
    wall_color: (wallColor ?? '').toLowerCase(),
    wall_orientation: (wallOrientation ?? '').toLowerCase(),
    wall_length: wallLength,
    wall_height: wallHeight,
    slatwall_enabled: slatwallEnabled,
    garage_door_included: garageDoorIncluded,
    total_walls: totalWalls,
    step_number: 2,
    step_name: 'Walls',
  });
}

// ============================================================================
// EVENT 15 — Wall added (Individual Walls)
// ============================================================================

export function trackWallAdded(totalWalls: number): void {
  push({
    event: 'estimator_wall_added',
    project_type: 'individual_walls',
    total_walls: totalWalls,
    step_number: 2,
    step_name: 'Walls',
  });
}

// ============================================================================
// EVENT 16 — Ceiling zone configured (Ceiling Only)
// ============================================================================

export function trackCeilingZoneConfigured(
  zoneIndex: number,
  zoneName: string,
  ceilingColor: string,
  zoneLength: number,
  zoneWidth: number,
  totalZones: number,
): void {
  push({
    event: 'estimator_ceiling_zone_configured',
    project_type: 'ceiling_only',
    zone_index: zoneIndex,
    zone_name: zoneName,
    ceiling_color: (ceilingColor ?? '').toLowerCase(),
    zone_length: zoneLength,
    zone_width: zoneWidth,
    total_zones: totalZones,
    step_number: 2,
    step_name: 'Ceiling',
  });
}

// ============================================================================
// EVENT 17 — Ceiling zone added (Ceiling Only)
// ============================================================================

export function trackZoneAdded(totalZones: number): void {
  push({
    event: 'estimator_zone_added',
    project_type: 'ceiling_only',
    total_zones: totalZones,
    step_number: 2,
    step_name: 'Ceiling',
  });
}

// ============================================================================
// EVENT 18 — Trim calculator used
// ============================================================================

export function trackTrimItemAdded(
  mode: CalculationMode,
  trimType: 'window' | 'door',
  trimWidth: number,
  trimHeight: number,
): void {
  push({
    event: 'estimator_trim_item_added',
    project_type: PROJECT_TYPE_MAP[mode],
    trim_type: trimType,
    trim_width: trimWidth,
    trim_height: trimHeight,
  });
}

// ============================================================================
// EVENT 19 — Currency toggled
// ============================================================================

export function trackCurrencyToggled(
  mode: CalculationMode,
  currency: string,
): void {
  push({
    event: 'estimator_currency_toggled',
    project_type: PROJECT_TYPE_MAP[mode],
    // The event's own key reports the NEW currency; the base context still
    // holds the old one until the caller updates it.
    currency,
  });
}

// ============================================================================
// EVENT 20 — PDF saved
// ============================================================================

export function trackPdfSaved(mode: CalculationMode, estimateResult: EstimateOutput): void {
  const { totalPanels, totalSqft } = bomTotals(estimateResult);
  push({
    event: 'estimator_pdf_saved',
    project_type: PROJECT_TYPE_MAP[mode],
    ...priceKeys(estimateResult),
    total_panels: totalPanels,
    total_sqft: totalSqft,
  });
}

// ============================================================================
// EVENT 21 — Results printed
// ============================================================================

export function trackResultsPrinted(mode: CalculationMode, estimateResult: EstimateOutput): void {
  const { totalPanels, totalSqft } = bomTotals(estimateResult);
  push({
    event: 'estimator_results_printed',
    project_type: PROJECT_TYPE_MAP[mode],
    ...priceKeys(estimateResult),
    total_panels: totalPanels,
    total_sqft: totalSqft,
  });
}

// ============================================================================
// EVENT 22 — Start over clicked
// ============================================================================

export function trackStartOver(mode: CalculationMode, estimateResult: EstimateOutput): void {
  push({
    event: 'estimator_start_over',
    project_type: PROJECT_TYPE_MAP[mode],
    ...priceKeys(estimateResult),
  });
}

// ============================================================================
// EVENT 23 — Project planner form submitted (Detailed Materials Breakdown)
// ============================================================================
// MUST fire only AFTER the save-estimate API returns success (the modal's
// "Estimate Saved Successfully!" state). Never fire on button click —
// Turnstile failures and network errors would create false conversion
// signals in Google Ads and corrupt bidding.

/** How the save prompt was opened. */
export type SaveTrigger = 'auto_prompt' | 'email_button';
/**
 * Which capture form the retailer's policy showed:
 *   save_modal      — minimal capture (name + email), GuestSaveModal
 *   project_planner — full lead form, GuestEstimateModal
 *   account         — a signed-in member's save (EstimateNameModal)
 */
export type CaptureMode = 'save_modal' | 'project_planner' | 'account';

export function trackProjectPlannerSubmitted(
  mode: CalculationMode,
  spaceType: string,
  estimateId: string,
  currency: 'USD' | 'CAD',
  estimateResult: EstimateOutput,
  // All optional: a retailer on a `minimal` capture policy only ever collects an
  // estimate name and an email, so most of these are genuinely not gathered.
  // Absent keys are pushed as '' rather than undefined, so the dataLayer shape
  // stays constant and GA4/BigQuery can tell "not asked" from "asked, skipped"
  // by segmenting on the retailer.
  contact: {
    userType?: string;        // "I am a" dropdown — selected label, passed exactly
    projectCategory?: string; // "This project is" radio — 'commercial' | 'residential'
    projectName?: string;     // "Project Name" free-text — raw value
    country?: string;         // "Country" dropdown — selected label
    region?: string;          // "Province / State / Region" dropdown — selected label
    city?: string;            // "City" free-text — raw value
  },
  capture: { captureMode: CaptureMode; saveTrigger: SaveTrigger | null } = { captureMode: 'project_planner', saveTrigger: null },
): void {
  const { totalPanels, totalSqft } = bomTotals(estimateResult);

  // Raw room dimensions (Step 4) — resolved from preset card or custom inputs.
  // Pushed as raw numbers so GA4/BigQuery can average and bucket them; the
  // formatted "40' L × 20' W × 10' H" string can't be aggregated. Length is
  // measured across the joists per the on-screen note.
  const { length, width, height } = estimateResult.input.roomDimensions;

  push({
    event: 'estimator_project_planner_submitted',
    project_type: PROJECT_TYPE_MAP[mode],
    ...priceKeys(estimateResult),
    total_panels: totalPanels,
    total_sqft: totalSqft,
    room_length_ft: length,
    room_width_ft: width,
    room_height_ft: height,
    space_type: spaceType,
    estimate_id: estimateId,
    currency,
    form_completion_percent: 100,
    capture_mode: capture.captureMode,
    save_trigger: capture.saveTrigger,
    // Contact form fields for GA4/BigQuery segmentation. Keys named per the
    // marketing team's spec; sourced from the modal's form state.
    user_type: contact.userType ?? '',
    project_category: contact.projectCategory ?? '',
    project_name: contact.projectName ?? '',
    country: contact.country ?? '',
    region: contact.region ?? '',
    city: contact.city ?? '',
  });
}

// ============================================================================
// EVENT 23b — Save-estimate modal opened / dismissed
// ============================================================================
// Dismissed = closed without a successful save, by any route (X, backdrop,
// Escape, "Don't save"). The caller decides; this module just reports.

export function trackSaveModalOpened(saveTrigger: SaveTrigger, captureMode: CaptureMode): void {
  push({
    event: 'estimator_save_modal_opened',
    save_trigger: saveTrigger,
    capture_mode: captureMode,
  });
}

export function trackSaveModalDismissed(saveTrigger: SaveTrigger, captureMode: CaptureMode): void {
  push({
    event: 'estimator_save_modal_dismissed',
    save_trigger: saveTrigger,
    capture_mode: captureMode,
  });
}

// ============================================================================
// EVENT 23c — Signed-in member saved an estimate
// ============================================================================
// Members never see the guest capture form, so their saves were invisible.

export function trackEstimateSaved(
  mode: CalculationMode,
  spaceType: string,
  estimateId: string,
  estimateResult: EstimateOutput,
): void {
  const { totalPanels, totalSqft } = bomTotals(estimateResult);
  push({
    event: 'estimator_estimate_saved',
    project_type: PROJECT_TYPE_MAP[mode],
    space_type: spaceType,
    estimate_id: estimateId,
    capture_mode: 'account' as CaptureMode,
    ...priceKeys(estimateResult),
    total_panels: totalPanels,
    total_sqft: totalSqft,
  });
}

// ============================================================================
// EVENT 24 — CSV exported
// ============================================================================

export function trackCsvExported(mode: CalculationMode, estimateResult: EstimateOutput): void {
  const { totalPanels, totalSqft } = bomTotals(estimateResult);
  push({
    event: 'estimator_csv_exported',
    project_type: PROJECT_TYPE_MAP[mode],
    ...priceKeys(estimateResult),
    total_panels: totalPanels,
    total_sqft: totalSqft,
  });
}

// ============================================================================
// EVENT 25 — Language changed (user action only)
// ============================================================================
// Fired from the switcher, not from boot resolution or cross-tab sync.

export function trackLanguageChanged(fromLocale: string, toLocale: string): void {
  push({
    event: 'estimator_language_changed',
    from_locale: fromLocale,
    to_locale: toLocale,
    // The event reports the NEW locale; the caller updates the context after.
    locale: toLocale,
  });
}

// ============================================================================
// EVENT 26 — Store locator search
// ============================================================================
// Fires once per data fetch of an admin-configured location map. The map id,
// its dataset and every location come from the maps registry, so a locator any
// retailer configures is tracked identically. `search_location` is the
// geocoder's own label (city / postal level); never a street address, and null
// for geolocation so coordinates never leak.

export type StoreSearchMethod = 'typed' | 'geolocate' | 'auto' | 'viewport';
export type LocatorType = 'modal' | 'inline';

export function trackStoreSearch(info: {
  mapId: number;
  locatorType: LocatorType;
  dataset: string;
  searchMethod: StoreSearchMethod;
  searchLocation: string | null;
  resultsCount: number;
  resultsTotal: number;
}): void {
  push({
    event: 'estimator_store_search',
    map_id: info.mapId,
    locator_type: info.locatorType,
    location_dataset: info.dataset,
    search_method: info.searchMethod,
    search_location: info.searchLocation,
    results_count: info.resultsCount,
    results_total: info.resultsTotal,
  });
}

// ============================================================================
// EVENT 27 — Store locator result click
// ============================================================================
// Card and popup actions. `store_number` is the location's Salesforce id from
// the maps API (falls back to its public id), which is what Gerson's "retailer
// id the map API returns" means. Business names, cities and regions are public
// listing data, not PII.

export type StoreResultAction = 'show_on_map' | 'marker' | 'call' | 'directions' | 'website' | 'profile';

export function trackStoreResultClick(info: {
  mapId: number;
  locatorType: LocatorType;
  action: StoreResultAction;
  location: { id: string; sfid: string; dataset: string; type: string; name: string; city: string; region: string; regionCode: string | null; country: string };
  distanceKm: number | null;
}): void {
  const l = info.location;
  push({
    event: 'estimator_store_result_click',
    map_id: info.mapId,
    locator_type: info.locatorType,
    action: info.action,
    store_number: l.sfid || l.id,
    store_name: l.name,
    store_city: l.city,
    store_region: l.regionCode || l.region,
    store_country: l.country,
    store_type: l.type,
    location_dataset: l.dataset,
    distance_km: info.distanceKm,
  });
}

// ============================================================================
// EVENT 28 — Retailer product link clicked
// ============================================================================
// A custom product field with a URL template renders as a link in the Bill of
// Materials / Placement Guide. Which field, and its values, are admin data per
// retailer; the event just reports what was clicked. Not scan_sheet: custom
// output tables carry no links.

export type ProductLinkSection = 'bom' | 'placement_guide';

export interface ProductLinkAnalytics {
  fieldKey: string;
  storeSku: string;
  trusscoreSku: string;
  productName: string;
  qty: number;
}

export function trackRetailerProductClick(link: ProductLinkAnalytics, section: ProductLinkSection, url: string): void {
  push({
    event: 'estimator_retailer_product_click',
    field_key: link.fieldKey,
    store_sku: link.storeSku,
    trusscore_sku: link.trusscoreSku,
    product_name: link.productName,
    qty: link.qty,
    section,
    link_url: url,
  });
}

// ============================================================================
// EVENT 29 — Custom output table viewed (scan sheets)
// ============================================================================
// "Scan sheet" is not a component: it is an admin-defined custom output table
// that happens to contain barcode blocks. Every such table reports once per
// results view when at least half of it is on screen. Gerson's tag can key on
// has_barcodes; the estimator_scan_sheet_viewed alias fires only for those.

export function trackOutputTableViewed(info: {
  tableKey: string;
  title: string;
  placement: string;
  hasBarcodes: boolean;
  skuCount: number;
}): void {
  const payload = {
    table_key: info.tableKey,
    table_title: info.title,
    table_placement: info.placement,
    has_barcodes: info.hasBarcodes,
    sku_count: info.skuCount,
  };
  push({ event: 'estimator_output_table_viewed', ...payload });
  if (info.hasBarcodes) push({ event: 'estimator_scan_sheet_viewed', ...payload });
}
